// src/lib/blog/config/topics.ts
//
// Topic definitions live in their OWN module (split out of `index.ts`) so each
// topic archive's sitemap `<lastmod>` can track that topic's OWN authored fields
// per-entry — NOT this whole file's git mtime (which would be uniform across all
// topics, the anti-pattern Google penalizes). `scripts/build-source-fingerprints.ts`
// hashes each topic's title/description/icon/OG fields into `source-fingerprints.json`,
// and `blogSitemapProvider` resolves a topic archive as
// `max(sourceLastModified, freshest(members))`. See
// `docs/seo/sitemap-lastmod-architecture.md`.

import type { Topic } from '~/lib/blog/types/blog.types'
import { DEFAULTS } from '~/lib/blog/config/defaults'

/**
 * Blog topics configuration
 */
export const TOPICS: Topic[] = [
  {
    title: 'Creative Analytics',
    slug: 'creative-analytics',
    href: '/blog/topics/creative-analytics',
    description: 'Master data-driven creative optimization with AI-powered analytics. Learn how to measure, benchmark and predict ad creative performance across digital channels using advanced analytics techniques.',
    featured: true,
    iconName: 'bar-chart-box',
    images: {
      og: {
        url: '/images/blog/adsights-blog-topic-creative-analytics-og-image-2400x1260.jpg',
        width: 2400,
        height: 1260,
        alt: 'AdSights Creative Analytics insights'
      }
    },
    routeMetadata: {
      path: '/blog/topics/creative-analytics',
      priority: 0.9,
      changefreq: 'daily'
    }
  },
  {
    title: 'Ad Performance',
    slug: 'ad-performance',
    href: '/blog/topics/ad-performance',
    description: 'Maximize your advertising ROI with expert strategies for performance optimization. Deep dive into metrics, attribution modeling, and proven techniques to boost engagement, conversions and campaign effectiveness.',
    featured: true,
    iconName: 'line-chart',
    images: {
      og: {
        url: '/images/blog/adsights-blog-topic-ad-performance-og-image-2400x1260.jpg',
        width: 2400,
        height: 1260,
        alt: 'AdSights Ad Performance insights'
      }
    },
    routeMetadata: {
      path: '/blog/topics/ad-performance',
      priority: 0.6,
      changefreq: 'daily'
    }
  },
  {
    title: 'Creative Testing',
    slug: 'creative-testing',
    href: '/blog/topics/creative-testing',
    description: 'Master the art and science of creative testing with robust methodologies. Learn advanced A/B testing frameworks, multivariate analysis, and experiment design to optimize your ad creative with statistical confidence.',
    featured: true,
    iconName: 'test-tube',
    routeMetadata: {
      path: '/blog/topics/creative-testing',
      priority: 0.9,
      changefreq: 'daily'
    }
  },
  {
    title: 'Creative Strategy',
    slug: 'creative-strategy',
    href: '/blog/topics/creative-strategy',
    description: 'Develop winning creative strategies backed by data and insights. Explore frameworks for creative ideation, brand storytelling, and campaign planning that drive measurable business results.',
    featured: true,
    iconName: 'lightbulb',
    images: {
      og: {
        url: '/images/blog/adsights-blog-topic-creative-strategy-og-image-2400x1260.jpg',
        width: 2400,
        height: 1260,
        alt: 'AdSights Creative Strategy insights'
      }
    },
    routeMetadata: {
      path: '/blog/topics/creative-strategy',
      priority: 0.9,
      changefreq: 'daily'
    }
  },
  {
    title: 'Industry Insights',
    slug: 'industry-insights',
    href: '/blog/topics/industry-insights',
    description: 'Stay ahead with expert analysis of digital advertising trends and innovations. Get actionable insights from industry research, benchmark reports, and thought leadership on the future of advertising.',
    featured: false,
    iconName: 'newspaper',
    routeMetadata: {
      path: '/blog/topics/industry-insights',
      priority: 0.5,
      changefreq: 'monthly'
    }
  },
  {
    title: 'AI in Advertising',
    slug: 'ai-advertising',
    href: '/blog/topics/ai-advertising',
    description: 'Harness the power of artificial intelligence to transform your advertising. Explore practical applications of machine learning for creative optimization, automated bidding, and predictive analytics.',
    featured: true,
    iconName: 'robot-2',
    routeMetadata: {
      path: '/blog/topics/ai-advertising',
      priority: 0.8,
      changefreq: 'weekly'
    }
  },
  {
    title: 'Campaign Management',
    slug: 'campaign-management',
    href: '/blog/topics/campaign-management',
    description: 'Master end-to-end campaign management with proven frameworks and tools. Learn strategic approaches to planning, execution, measurement and optimization across channels and platforms.',
    featured: false,
    iconName: 'dashboard',
    routeMetadata: {
      path: '/blog/topics/campaign-management',
      priority: 0.5,
      changefreq: 'monthly'
    }
  },
  {
    title: 'Social Media Advertising',
    slug: 'social-media-advertising',
    href: '/blog/topics/social-media-advertising',
    description: 'Dominate social media advertising with platform-specific strategies and insights. Expert guidance on creative best practices, targeting, and optimization for Facebook, Instagram, TikTok, LinkedIn and emerging channels.',
    featured: false,
    iconName: 'global',
    routeMetadata: {
      path: '/blog/topics/social-media-advertising',
      priority: 0.6,
      changefreq: 'monthly'
    }
  },
  {
    title: 'Ad Tech',
    slug: 'ad-tech',
    href: '/blog/topics/ad-tech',
    description: 'Navigate the complex world of advertising technology and automation. Comprehensive analysis of programmatic platforms, mar-tech stacks, and emerging solutions for modern advertisers.',
    featured: false,
    iconName: 'code',
    routeMetadata: {
      path: '/blog/topics/ad-tech',
      priority: 0.7,
      changefreq: 'weekly'
    }
  },
  {
    title: 'Media Buying',
    slug: 'media-buying',
    href: '/blog/topics/media-buying',
    description: 'Optimize your media investments with data-driven buying strategies. Learn advanced approaches to media planning, budget allocation, and cross-channel optimization for maximum ROI.',
    featured: false,
    iconName: 'money-dollar-circle',
    routeMetadata: {
      path: '/blog/topics/media-buying',
      priority: 0.7,
      changefreq: 'weekly'
    }
  },
  {
    title: 'Data Science',
    slug: 'data-science',
    href: '/blog/topics/data-science',
    description: 'Leverage data science to unlock advertising insights and opportunities. Expert guidance on statistical analysis, machine learning, predictive modeling and algorithmic optimization.',
    featured: false,
    iconName: 'test-tube',
    images: {
      og: {
        url: '/images/blog/adsights-blog-topic-data-science-og-image-2400x1260.jpg',
        width: 2400,
        height: 1260,
        alt: 'AdSights Data Science insights'
      }
    },
    routeMetadata: {
      path: '/blog/topics/data-science',
      priority: 0.4,
      changefreq: 'monthly'
    }
  },
  {
    title: 'Data Visualization',
    slug: 'data-visualization',
    href: '/blog/topics/data-visualization',
    description: 'Choose the right chart for marketing metrics — line vs bar, funnels, retention curves, scatter plots, and cohort heatmaps. Pillar: the interactive Which Chart for Which Metric guide with research-backed dashboard rules.',
    featured: true,
    iconName: 'pie-chart',
    images: {
      og: {
        url: '/images/resources/guides/marketing-data-visualization/adsights-marketing-data-visualization-guide-og-image_AR-16x9_2400x1350_2x.jpg',
        width: 2400,
        height: 1350,
        alt: 'Marketing data visualization — chart selection for analytics dashboards'
      }
    },
    routeMetadata: {
      path: '/blog/topics/data-visualization',
      priority: 0.8,
      changefreq: 'weekly'
    }
  },
  {
    title: 'Company News',
    slug: 'company-news',
    href: '/blog/topics/company-news',
    description: 'Stay updated with the latest from AdSights. Product launches, feature releases, company milestones, and announcements about new tools and resources we\'re building for the advertising community.',
    featured: false,
    iconName: 'megaphone',
    images: {
      og: {
        url: '/images/blog/adsights-blog-topic-company-news-og-image-2400x1260.jpg',
        width: 2400,
        height: 1260,
        alt: 'AdSights Company News'
      }
    },
    routeMetadata: {
      path: '/blog/topics/company-news',
      priority: 0.4,
      changefreq: 'weekly'
    }
  },
  {
    title: 'General',
    slug: 'general',
    href: '/blog/topics/general',
    description: DEFAULTS.TOPIC_DESCRIPTION,
    featured: false,
    iconName: 'article',
    routeMetadata: {
      path: '/blog/topics/general',
      priority: 0.2,
      changefreq: 'monthly'
    }
  }
]
